import React, { useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Checkbox from '@cloudscape-design/components/checkbox';
import { updateJobStatus, updateMapping, FieldMapping } from '../services/api';

export interface ApprovalWorkflowProps {
  jobId: string;
  mappings: FieldMapping[];
  onAction: () => void;
  hasDuplicates?: boolean;
}

/** Validate that a complete/reject action payload is well-formed */
export function validateAction(action: 'approve' | 'reject' | 'complete', reason: string): string | null {
  if (action === 'reject' && !reason.trim()) return 'A reason is required when rejecting a job';
  return null;
}

/** Build the request body for a mapping update */
export function buildMappingPayload(targetField: string, mappedValue?: string) {
  const payload: { targetField: string; mappedValue?: string } = { targetField };
  if (mappedValue !== undefined && mappedValue !== '') payload.mappedValue = mappedValue;
  return payload;
}

const ApprovalWorkflow: React.FC<ApprovalWorkflowProps> = ({ jobId, mappings, onAction, hasDuplicates }) => {
  const [confirmAction, setConfirmAction] = useState<'complete' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(true);
  const [templateNotes, setTemplateNotes] = useState('');

  // Inline editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState('');
  const [editValue, setEditValue] = useState('');

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const validationError = validateAction(confirmAction, reason);
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError(null);
    try {
      await updateJobStatus(
        jobId,
        confirmAction,
        confirmAction === 'reject' ? reason : undefined,
        undefined,
        confirmAction === 'complete' ? saveAsTemplate : undefined,
        confirmAction === 'complete' && saveAsTemplate ? (templateNotes || undefined) : undefined,
      );
      setConfirmAction(null);
      setReason('');
      setSaveAsTemplate(true);
      setTemplateNotes('');
      onAction();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMappingSave = async (sourceField: string) => {
    setRegenerating(true);
    setError(null);
    try {
      await updateMapping(jobId, sourceField, editTarget, editValue || undefined);
      setEditingField(null);
      onAction(); // reload to show regeneration status
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <SpaceBetween size="l">
      <Alert type="info">
        Approving types releases the individual records to be sent to SwiftID for processing.
      </Alert>

      {error && <StatusIndicator type="error">{error}</StatusIndicator>}
      {regenerating && <StatusIndicator type="loading">Regenerating output…</StatusIndicator>}

      <SpaceBetween direction="horizontal" size="s">
        <Button variant="primary" onClick={() => setConfirmAction('complete')} disabled={hasDuplicates}>Complete</Button>
        <Button onClick={() => setConfirmAction('reject')}>Reject</Button>
      </SpaceBetween>

      {/* Inline mapping editor */}
      <Table
        items={mappings}
        columnDefinitions={[
          { id: 'sourceField', header: 'Source', cell: (m: FieldMapping) => m.sourceField },
          { id: 'targetField', header: 'Target', cell: (m: FieldMapping) =>
            editingField === m.sourceField
              ? <Input value={editTarget} onChange={({ detail }: { detail: { value: string } }) => setEditTarget(detail.value)} />
              : m.targetField,
          },
          { id: 'mappedValue', header: 'Mapped Value', cell: (m: FieldMapping) =>
            editingField === m.sourceField
              ? <Input value={editValue} onChange={({ detail }: { detail: { value: string } }) => setEditValue(detail.value)} />
              : m.mappedValue,
          },
          { id: 'actions', header: 'Edit', cell: (m: FieldMapping) =>
            editingField === m.sourceField
              ? <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => handleMappingSave(m.sourceField)}>Save</Button>
                  <Button variant="link" onClick={() => setEditingField(null)}>Cancel</Button>
                </SpaceBetween>
              : <Button variant="icon" iconName="edit" onClick={() => {
                  setEditingField(m.sourceField);
                  setEditTarget(m.targetField);
                  setEditValue(m.mappedValue);
                }} />,
          },
        ]}
        empty="No mappings"
      />

      {/* Confirmation modal */}
      <Modal
        visible={confirmAction !== null}
        onDismiss={() => { setConfirmAction(null); setReason(''); setError(null); }}
        header={confirmAction === 'complete' ? 'Confirm Completion' : 'Confirm Rejection'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => { setConfirmAction(null); setReason(''); setSaveAsTemplate(true); setTemplateNotes(''); }}>Cancel</Button>
              <Button variant="primary" loading={submitting} onClick={handleConfirm}>
                {confirmAction === 'complete' ? 'Complete' : 'Reject'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {confirmAction === 'complete' && (
            <SpaceBetween size="m">
              <Box>Are you sure you want to complete this job?</Box>
              <Checkbox
                checked={saveAsTemplate}
                onChange={({ detail }) => setSaveAsTemplate(detail.checked)}
              >
                Also save as template
              </Checkbox>
              {saveAsTemplate && (
                <FormField label="Template notes (optional)">
                  <Textarea
                    value={templateNotes}
                    onChange={({ detail }: { detail: { value: string } }) => setTemplateNotes(detail.value)}
                    placeholder="e.g. Broker X standard format, verified April 2026"
                  />
                </FormField>
              )}
            </SpaceBetween>
          )}
          {confirmAction === 'reject' && (
            <FormField label="Rejection reason" errorText={error}>
              <Textarea value={reason} onChange={({ detail }: { detail: { value: string } }) => setReason(detail.value)} placeholder="Provide a reason for rejection" />
            </FormField>
          )}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default ApprovalWorkflow;
